import { createFileRoute } from '@tanstack/react-router';
import {
  Card,
  CardContent,
  CardMedia,
  Chip,
  Container,
  Grid,
} from '@mui/material';
import { useSuspenseQuery } from '@apollo/client';
import { campaignDocument } from '@social-garden/api/documents/campaign.ts';
import { campaignStatisticsForCampaignDocument } from '@social-garden/api/documents/campaignStatistics.ts';
import { getUriWithHash } from '@social-garden/utils/helpers.ts';
import { getContentTypeFromCampaignType } from '@social-garden/utils/content.ts';
import { useTranslation } from 'react-i18next';
import Brand from '@/components/Brand.tsx';
import CampaignStatistics from '@/components/CampaignStatistics.tsx';
import useCampaignStatus from '@/hooks/useCampaignStatus.ts';
import PageTitle from '@/components/PageTitle.tsx';
import useCategory from '@/hooks/useCategory.ts';
import useCountry from '@/hooks/useCountry.ts';
import ChannelTypeChip from '@/components/ChannelTypeChip.tsx';
import ChannelContentTypeChip from '@/components/ChannelContentTypeChip.tsx';
import CampaignDetailsActionButton from '@/components/CampaignDetails/CampaignDetailsActionButton.tsx';
import CreatorsOfCampaign from '@/components/CampaignDetails/CreatorsOfCampaign.tsx';

export const Route = createFileRoute('/_manager/_app/campaigns/$id/')({
  component: CampaignDetails,
});

function CampaignDetails() {
  const { id } = Route.useParams();
  const {
    t,
    i18n: { resolvedLanguage },
  } = useTranslation(['common']);

  const {
    data: { campaign },
  } = useSuspenseQuery(campaignDocument, {
    variables: {
      id,
    },
  });

  const {
    data: { campaignStatisticsForCampaign },
  } = useSuspenseQuery(campaignStatisticsForCampaignDocument, {
    variables: {
      campaignId: id,
    },
  });

  const { getCampaignStatusName } = useCampaignStatus();
  const { categoryNames } = useCategory();
  const { countryNames } = useCountry();

  return (
    <Container>
      <Grid container spacing={4}>
        <Grid size={12}>
          <Card variant="outlined">
            <CardMedia
              sx={{
                aspectRatio: 21 / 9,
                width: '100%',
              }}
              image={getUriWithHash(campaign.headerImage)}
              title={campaign.name}
              draggable={false}
            />
            <CardContent>
              <Grid container spacing={2}>
                <Grid container alignItems="center" size={12}>
                  <Grid
                    size={{
                      xs: 12,
                      md: 'grow',
                    }}>
                    <PageTitle>{campaign.name}</PageTitle>
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      md: 'auto',
                    }}>
                    <CampaignDetailsActionButton campaign={campaign} />
                  </Grid>
                </Grid>
                <Grid size={12}>
                  <Brand
                    name={campaign.brand.name}
                    logo={campaign.brand.logo}
                  />
                </Grid>
                <Grid container size={12} spacing={1}>
                  <Grid size="auto">
                    <Chip
                      label={getCampaignStatusName(campaign.status)}
                      variant="filled"
                      size="small"
                    />
                  </Grid>
                  <Grid size="auto">
                    <Chip
                      label={categoryNames[campaign.category]}
                      variant="filled"
                      size="small"
                    />
                  </Grid>
                  {campaign.targetCountries.length > 0 ? (
                    campaign.targetCountries
                      .toSorted((a, b) =>
                        countryNames[a].localeCompare(
                          countryNames[b],
                          resolvedLanguage,
                        ),
                      )
                      .map((country) => (
                        <Grid key={country} size="auto">
                          <Chip
                            label={countryNames[country]}
                            variant="filled"
                            size="small"
                          />
                        </Grid>
                      ))
                  ) : (
                    <Grid size="auto">
                      <Chip
                        label={t('common:global')}
                        variant="filled"
                        size="small"
                      />
                    </Grid>
                  )}
                  <Grid size="auto">
                    <ChannelTypeChip
                      channelType={campaign.typeable.channelType}
                      size="small"
                    />
                  </Grid>
                  <Grid size="auto">
                    <ChannelContentTypeChip
                      channelType={campaign.typeable.channelType}
                      channelContentType={getContentTypeFromCampaignType(
                        campaign.typeable,
                      )}
                      size="small"
                    />
                  </Grid>
                  <Grid size="auto">
                    <Chip
                      label={
                        campaign.isDiscoverable
                          ? t('common:campaign.isDiscoverable')
                          : t('common:campaign.isNotDiscoverable')
                      }
                      variant="filled"
                      size="small"
                      color={campaign.isDiscoverable ? 'info' : 'warning'}
                    />
                  </Grid>
                </Grid>
                {campaignStatisticsForCampaign ? (
                  <Grid size={12}>
                    <CampaignStatistics
                      statistics={campaignStatisticsForCampaign}
                    />
                  </Grid>
                ) : null}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={12}>
          <CreatorsOfCampaign campaignId={campaign.id} />
        </Grid>
      </Grid>
    </Container>
  );
}
